import { Facebook, Instagram, Linkedin } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { BRAND } from '../theme/brand'

const navigation = {
  main: [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Holiday Packages', href: '/packages' },
    { name: 'Europe Tours', href: '/europamundo' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact us', href: '/contact' },
  ],
}

export default function Footer() {
  const { franchiseData, loading } = useFranchiseData()

  if (loading || !franchiseData) {
    return (
      <footer className="bg-white border-t border-gray-200">
        <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-48 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-32"></div>
          </div>
        </div>
      </footer>
    )
  }

  const accentColor = BRAND.maroon

  const socialLinks = [
    {
      name: 'Facebook',
      href: franchiseData.facebook || '#',
      icon: Facebook,
    },
    {
      name: 'Instagram',
      href: franchiseData.instagram || '#',
      icon: Instagram,
    },
    {
      name: 'LinkedIn',
      href: franchiseData.linkedin || '#',
      icon: Linkedin,
    },
  ].filter((link) => link.href !== '#')

  return (
    <footer
      className="text-gray-200"
      style={{
        background: `radial-gradient(900px circle at 50% 35%, rgba(255,255,255,0.04) 0%, transparent 60%), linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.maroon} 100%)`,
      }}
    >
      <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4 lg:items-start">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <p className="mt-4 text-base text-gray-300">
              Your trusted travel partner for unforgettable experiences around the world. We create
              memories that last a lifetime.
            </p>
            <div className="mt-4 flex items-center">
              <span style={{ color: accentColor }} className="font-bold mr-2">🇿🇦</span>
              <span className="text-white font-semibold">Proudly South African</span>
            </div>
            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div className="mt-6 flex space-x-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 transition-colors"
                    onMouseEnter={(e) => (e.currentTarget.style.color = accentColor)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '')}
                  >
                    <span className="sr-only">{link.name}</span>
                    <link.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
              Quick Links
            </h3>
            <ul className="space-y-4">
              {navigation.main.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    className="text-gray-300 transition-colors text-base"
                    onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.taupe)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '')}
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
              Contact Info
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-base text-gray-300">
                <span style={{ color: BRAND.taupe }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="inline h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a2 2 0 011.7.95l1.54 2.6a2 2 0 01-.17 2.18l-1.1 1.65a11.042 11.042 0 005.516 5.516l1.65-1.1a2 2 0 012.18-.17l2.6 1.54a2 2 0 01.95 1.7V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V9a2 2 0 012-2z" />
                  </svg>
                </span>
                <div className="flex flex-col leading-snug">
                  <a
                    href="tel:+27731754254"
                    className="hover:opacity-80 transition-opacity"
                  >
                    Mobile: +27731754254
                  </a>
                  <a
                    href="tel:0123615924"
                    className="hover:opacity-80 transition-opacity"
                  >
                    Office: 0123615924
                  </a>
                  <a
                    href="tel:0218795729"
                    className="hover:opacity-80 transition-opacity"
                  >
                    0218795729
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-base text-gray-300">
                <span style={{ color: BRAND.taupe }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="inline h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12H8m8 0a4 4 0 11-8 0 4 4 0 018 0zm0 0v1a4 4 0 01-8 0v-1" />
                  </svg>
                </span>
                <span>{franchiseData.email}</span>
              </div>
              <div className="flex items-start space-x-2 text-base text-gray-300">
                  <span style={{ color: BRAND.taupe }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="inline h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 21l-4.243-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </span>
                  <span>Lynnwood Ridge, Pretoria</span>
                </div>
              <div className="text-sm text-gray-400 mt-2 space-y-1">
                <div>Working Hours</div>
                <div>Monday – Friday: 9:00 AM – 5:00 PM</div>
                <div>Saturday: 9:00 AM – 1:00 PM</div>
                <div>Sunday: Closed</div>
              </div>
            </div>
          </div>

          {/* Logo Section */}
          <div className="lg:col-span-1 flex items-center justify-center">
            <img
              src="/image-removebg-preview.png"
              alt="2Life Travel Logo"
              className="h-28 sm:h-32 w-auto mt-5 sm:mt-6"
              style={{ filter: 'brightness(1.05) contrast(1.05)' }}
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 border-t border-[#232b36] pt-8">
          <div className="flex justify-center text-center">
            <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} 2Life Travel. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  )
}